import moment from 'moment';
import styled from '@emotion/styled';
import { ReactNode, useCallback, useState } from 'react';

import { subscriptionStatuses } from '@selfserve/react-utils';
import { StyledText } from '@selfserve/react-ui';

import { subscriptionStatusesTexts } from '../constants/status-texts';
import { subscriptionStatusColors } from '../constants/status-colors';

import { StyledStatus } from './admin-styled-components';

const StyledPlansTooltipContainer = styled('div')(
  ({ theme }: { theme?: any }) => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  })
);

const StyledPlansButton = styled('div')(({ theme }: { theme?: any }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '30px',
  height: '24px',
  marginLeft: '8px',
  borderRadius: '4px',
  cursor: 'pointer',
  color: theme.palette.grey[100],
  backgroundColor: theme.palette.grey[700],
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 700,
  '&:hover': {
    backgroundColor: theme.palette.grey[400],
  },
}));

const StyledPlansListContainer = styled('div')(
  ({ theme }: { theme?: any }) => ({
    minWidth: '348px',
    width: 'max-content',
    position: 'absolute',
    backgroundColor: '#FFF',
    padding: '24px',
    borderRadius: '8px',
    border: `1px solid ${theme.palette.grey[400]}`,
    boxShadow:
      '0px 0px 0px 1px rgba(0, 0, 0, 0), 0px 4px 30px -8px rgba(0, 0, 0, 0.5)',
    zIndex: 2,
    bottom: '36px',
  })
);

const StyledPlanInfoPair = styled('div')(({ theme }: { theme?: any }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  '& + &': {
    marginTop: '8px',
  },
}));

const StyledPlanName = styled(StyledText)(({ theme }: { theme?: any }) => ({
  margin: '0 auto 0 0',
}));

const StyledEndDate = styled('span')(({ theme }: { theme?: any }) => ({
  marginLeft: theme.spacing(1),
}));

interface Props {
  plans: any[];
  children: ReactNode;
}

export const AdminPlansTooltip = ({ children, plans }: Props) => {
  const [isShown, setIsShown] = useState(false);

  const handleShow = useCallback(
    (value: boolean) => () => setIsShown(value),
    []
  );

  if (!plans || plans.length === 1) return <>{children}</>;

  return (
    <StyledPlansTooltipContainer>
      {children}
      <StyledPlansButton
        onMouseEnter={handleShow(true)}
        onMouseLeave={handleShow(false)}
      >
        +{plans.length - 1}
        {isShown && (
          <StyledPlansListContainer>
            {plans.map(
              ({ plan: { displayName, name }, status, _id, trial_end }) => (
                <StyledPlanInfoPair key={_id}>
                  <StyledPlanName isSmall bold>
                    {displayName || name}
                  </StyledPlanName>
                  <StyledStatus
                    withMargin
                    className={subscriptionStatusColors[status]}
                  >
                    {subscriptionStatusesTexts[status]}
                  </StyledStatus>
                  {trial_end && status === subscriptionStatuses.IN_TRIAL && (
                    <StyledEndDate>
                      {moment.unix(trial_end).format('MMM DD, YYYY')}
                    </StyledEndDate>
                  )}
                </StyledPlanInfoPair>
              )
            )}
          </StyledPlansListContainer>
        )}
      </StyledPlansButton>
    </StyledPlansTooltipContainer>
  );
};
